import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../lib/apiClient";
import { FORM_MUTATION_META } from "../utils/formErrorHandler";
import type {
  AuditorGroupWithCounts,
  AuditorGroupDetail,
  CreateAuditorGroupData,
  UpdateAuditorGroupData,
} from "../types/auditor";

// Types used locally in this file — re-export the local imports.
export type {
  AuditorGroupWithCounts,
  AuditorGroupDetail,
  CreateAuditorGroupData,
  UpdateAuditorGroupData,
};

// Types not used locally — direct re-export from source.
export type {
  AuditorGroup,
  AuditorInvitationRequest,
  AuditorGroupMember,
} from "../types/auditor";

/**
 * Fetch all auditor groups (admin only)
 */
export function useAuditorGroups() {
  return useQuery({
    queryKey: ["admin", "auditor-groups"],
    queryFn: async () => {
      const { data } = await api.get("/admin/auditor-groups");
      return data.data as AuditorGroupWithCounts[];
    },
  });
}

/**
 * Fetch single auditor group with details
 */
export function useAuditorGroup(id: string | undefined) {
  return useQuery({
    queryKey: ["admin", "auditor-groups", id],
    queryFn: async () => {
      const { data } = await api.get(`/admin/auditor-groups/${id}`);
      return data.data as AuditorGroupDetail;
    },
    enabled: !!id,
  });
}

/**
 * Create new auditor group with manager user
 */
export function useCreateAuditorGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (auditorGroupData: CreateAuditorGroupData) => {
      const { data } = await api.post(
        "/admin/auditor-groups",
        auditorGroupData,
      );
      return data.data;
    },
    // US-081: AuditorGroupForm catches with handleFormErrors (US-080 DOM-8).
    meta: FORM_MUTATION_META,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "auditor-groups"] });
      // US-153/F-076: creating a group SENDS a manager invitation (Manager Email
      // and Manager Name are required fields), so the pending list changes too.
      // Its key is ["auditor-manager-invitations", status] — a different key from
      // the one above, and Mantine Tabs keeps the panel mounted, so the pending
      // query had already cached [] before the group existed. The tab then read
      // "No pending auditor manager invitations found" about an invitation that
      // had just been sent, and this is the only place an admin can retrieve the
      // link. The resend/delete mutations always invalidated it; create did not.
      queryClient.invalidateQueries({
        queryKey: ["auditor-manager-invitations"],
      });
    },
  });
}

/**
 * Update auditor group
 */
export function useUpdateAuditorGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: UpdateAuditorGroupData & { id: string }) => {
      const { data } = await api.put(`/admin/auditor-groups/${id}`, updates);
      return data.data;
    },
    // US-081: AuditorGroupForm catches with handleFormErrors (US-080 DOM-8).
    meta: FORM_MUTATION_META,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "auditor-groups"] });
      queryClient.invalidateQueries({
        queryKey: ["admin", "auditor-groups", variables.id],
      });
    },
  });
}
